import { API_BASE_URL } from '../config';
import React, { useState, useEffect, useCallback } from 'react';
import DoctorAppointmentDetailModal from './DoctorAppointmentDetailModal';
import ClinicalReports from './ClinicalReports';

const PatientDetailModal = ({ patient, onClose }) => {
  const [history, setHistory] = useState([]);
  const [bills, setBills] = useState({ invoices: [], sales: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVisit, setSelectedVisit] = useState(null);
  const canEdit = canEditVisits();

  const loadChart = useCallback(async () => {
    if (!patient || !patient._id) return;
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setLoading(false);
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [visitsResponse, chartResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/appointments/patient/${patient._id}`, { headers }),
        fetch(`${API_BASE_URL}/doctors/patients/${patient._id}/chart`, { headers })
      ]);
      const visits = visitsResponse.ok ? await visitsResponse.json() : [];
      const chart = chartResponse.ok ? await chartResponse.json() : { invoices: [], sales: [] };
      setHistory(Array.isArray(visits) ? visits : []);
      setBills(chart && Array.isArray(chart.invoices) ? chart : { invoices: [], sales: [] });
      if (!visitsResponse.ok && visitsResponse.status !== 404) {
        const body = await visitsResponse.json().catch(() => ({}));
        setError(body.message || 'Could not load appointment history.');
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
      setHistory([]);
      setBills({ invoices: [], sales: [] });
    } finally {
      setLoading(false);
    }
  }, [patient]);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  if (!patient) return null;

  const latestVisit = history.find((visit) => visit.VITALS && Object.values(visit.VITALS).some(Boolean));
  const vitals = (latestVisit && latestVisit.VITALS) || patient.VITALS || {};
  const weight = Number(vitals.WEIGHT);
  const height = Number(vitals.HEIGHT);
  const bmi = weight > 0 && height > 0 ? (weight / ((height / 100) ** 2)).toFixed(1) : '';
  const activeMedicines = [];
  const procedures = [];
  const labTests = [];
  history.forEach((visit) => {
    (visit.DR_MEDICINES || []).forEach((plan) => {
      if (plan && (!plan.STATUS || plan.STATUS === 'Active')) {
        activeMedicines.push({ plan, date: visit.APPOINTMENT_DATE || plan.PRESCRIPTION_DATE });
      }
    });
    (visit.DR_PROCEDURES || []).forEach((plan) => {
      procedures.push({ plan, date: plan.PROCEDURE_DATE || visit.APPOINTMENT_DATE });
    });
    (visit.DR_LAB_TESTS || []).forEach((plan) => {
      labTests.push({ plan, date: plan.TEST_DATE || visit.APPOINTMENT_DATE });
    });
  });
  const billRows = [
    ...(bills.invoices || []).map((invoice) => ({
      date: invoice.INVOICE_DATE,
      kind: 'Invoice',
      ref: invoice.INVOICE_ID,
      status: invoice.INVOICE_STATUS,
      payment: `${invoice.PAYMENT_STATUS || ''} ${invoice.PAYMENT_METHOD || ''}`.trim(),
      items: (invoice.LINE_ITEMS || []).map((item) => item.ITEM_NAME).filter(Boolean).join(', '),
      tax: invoice.TAX_AMOUNT,
      total: invoice.TOTAL_AMOUNT
    })),
    ...(bills.sales || []).map((sale) => ({
      date: sale.SALE_DATE,
      kind: sale.SALE_TYPE || 'Sale',
      ref: sale.SALE_ID,
      status: sale.SALE_STATUS,
      payment: `${sale.PAYMENT_STATUS || ''} ${sale.PAYMENT_METHOD || ''}`.trim(),
      items: (sale.LINE_ITEMS || []).map((item) => item.ITEM_NAME).filter(Boolean).join(', '),
      tax: sale.TAX_AMOUNT,
      total: sale.TOTAL_AMOUNT
    }))
  ].sort((left, right) => new Date(right.date) - new Date(left.date));

  const detailRows = [
    ['Patient ID', patient.PATIENT_ID],
    ['Phone', patient.PATIENT_PHONE],
    ['Address', patient.PATIENT_ADDRESS],
    ['Date of birth', patient.DATE_OF_BIRTH ? new Date(patient.DATE_OF_BIRTH).toLocaleDateString() : ''],
    ['Email', patient.EMAIL],
    ['Emergency contact', [patient.EMERGENCY_CONTACT, patient.EMERGENCY_CONTACT_PHONE].filter(Boolean).join(' · ')],
    ['Insurance', [patient.INSURANCE_PROVIDER, patient.INSURANCE_ID].filter(Boolean).join(' · ')]
  ].filter(([, value]) => value);

  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle} className="patient-chart">
        <style>{'.patient-chart label{display:block;font-size:.85rem;color:#495057;margin-bottom:.15rem}'}</style>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
          <div>
            <h3 style={{ margin: '0 0 0.25rem' }}>{patient.PATIENT_NAME || 'Patient'}</h3>
            <p style={{ margin: 0, color: '#495057' }}>
              {[patient.PATIENT_AGE ? `${patient.PATIENT_AGE} yrs` : '', patient.PATIENT_GENDER, patient.BLOOD_TYPE ? `Blood ${patient.BLOOD_TYPE}` : '', patient.PATIENT_PHONE].filter(Boolean).join(' · ')}
            </p>
            {patient.PATIENT_ID && <p style={{ margin: '0.25rem 0 0', color: '#6c757d', fontSize: '0.9rem' }}>{patient.PATIENT_ID}</p>}
          </div>
          <button type="button" onClick={onClose} style={closeButton}>Close</button>
        </div>
        {patient.ALLERGIES && <p style={{ margin: '0.75rem 0 0', padding: '8px 10px', backgroundColor: '#fff3cd', borderRadius: '4px' }}><strong>Allergies:</strong> {patient.ALLERGIES}</p>}
        {patient.MEDICAL_HISTORY && patient.MEDICAL_HISTORY !== 'SAMPLE-YEAR' && <p style={{ margin: '0.5rem 0 0', color: '#495057' }}><strong>History:</strong> {patient.MEDICAL_HISTORY}</p>}
        {error && <p style={{ color: 'red' }}>{error}</p>}

        {detailRows.length > 0 && (
          <section style={panelStyle}>
            <h4 style={sectionTitle}>Registration</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
              {detailRows.map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: '0.85rem', color: '#495057' }}>{label}</div>
                  <div>{value}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section style={panelStyle}>
          <h4 style={sectionTitle}>Latest vitals{latestVisit && latestVisit.APPOINTMENT_DATE ? ` · ${new Date(latestVisit.APPOINTMENT_DATE).toLocaleDateString()}` : ''}</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
            {[
              ['Blood pressure', vitals.BLOOD_PRESSURE],
              ['Pulse', vitals.PULSE],
              ['Temp °F', vitals.TEMPERATURE],
              ['Weight kg', vitals.WEIGHT],
              ['Height cm', vitals.HEIGHT],
              ['SpO2 %', vitals.SPO2],
              ['Resp. rate', vitals.RESPIRATORY_RATE],
              ['BMI', bmi]
            ].map(([label, value]) => (
              <label key={label}>{label}
                <input value={value ?? ''} readOnly style={fieldStyle} />
              </label>
            ))}
          </div>
        </section>

        <section style={panelStyle}>
          <h4 style={sectionTitle}>Active medicine plan</h4>
          {loading ? <p>Loading medicines...</p> : (
            <ChartTable
              headers={['Medicine', 'Dose', 'Frequency', 'Duration', 'Route', 'Since']}
              rows={activeMedicines.map(({ plan, date }) => [
                plan.MEDICINE_ID?.MEDICINE_NAME || 'Medicine',
                `${plan.MEDICINE_DOSAGE || ''} ${plan.DOSAGE_UNIT || ''}`.trim(),
                `${plan.DOSAGE_FREQUENCY || ''}${(plan.DOSAGE_TIME || []).length ? ` (${plan.DOSAGE_TIME.join(', ')})` : ''}`,
                plan.DURATION ? `${plan.DURATION} days` : '',
                plan.MEDICINE_ROUTE || '',
                date ? new Date(date).toLocaleDateString() : ''
              ])}
            />
          )}
        </section>

        <section style={panelStyle}>
          <h4 style={sectionTitle}>Appointment history</h4>
          {loading ? <p>Loading history...</p> : (
            <ChartTable
              headers={['Date', 'Time', 'Doctor', 'Status', 'Vitals', 'Symptoms', 'Diagnosis', 'Medicines', '']}
              rows={history.map((visit) => [
                visit.APPOINTMENT_DATE ? new Date(visit.APPOINTMENT_DATE).toLocaleDateString() : '',
                visit.APPOINTMENT_SLOT || '',
                visit.DOCTOR_ID?.DOCTOR_NAME || visit.DOCTOR_NAME || '',
                visit.APPOINTMENT_STATUS || '',
                vitalSummary(visit.VITALS),
                visit.PATIENT_SYMPTOMS || '',
                visit.DOCTOR_COMMENTS || '',
                (visit.DR_MEDICINES || []).map((plan) => plan.MEDICINE_ID?.MEDICINE_NAME || 'Medicine').join(', '),
                <button type="button" key={visit._id} onClick={() => setSelectedVisit(visit)} style={openButton}>Open</button>
              ])}
            />
          )}
        </section>

        <section style={panelStyle}>
          <h4 style={sectionTitle}>Procedures</h4>
          {loading ? <p>Loading procedures...</p> : (
            <ChartTable
              headers={['Date', 'Procedure', 'Sitting', 'Status', 'Room', 'Therapist']}
              rows={procedures.map(({ plan, date }) => [
                date ? new Date(date).toLocaleDateString() : '',
                plan.PROCEDURE_ID?.PROCEDURE_NAME || plan.PROCEDURE_NAME || 'Procedure',
                plan.SESSION_TOTAL > 1 ? `${plan.SESSION_NUMBER || 1} of ${plan.SESSION_TOTAL}` : '1',
                plan.PROCEDURE_STATUS || '',
                plan.ROOM_NAME || '',
                plan.THERAPIST_NAME || ''
              ])}
            />
          )}
        </section>

        <section style={panelStyle}>
          <h4 style={sectionTitle}>Lab tests</h4>
          {loading ? <p>Loading lab tests...</p> : (
            <ChartTable
              headers={['Date', 'Test', 'Status', 'Priority']}
              rows={labTests.map(({ plan, date }) => [
                date ? new Date(date).toLocaleDateString() : '',
                plan.LAB_TEST_ID?.TEST_NAME || 'Lab test',
                plan.TEST_STATUS || '',
                plan.PRIORITY || ''
              ])}
            />
          )}
        </section>

        <section style={panelStyle}>
          <h4 style={sectionTitle}>Billing</h4>
          {loading ? <p>Loading bills...</p> : (
            <ChartTable
              headers={['Date', 'Type', 'Reference', 'Status', 'Payment', 'Items', 'Tax', 'Total']}
              rows={billRows.map((row) => [
                row.date ? new Date(row.date).toLocaleDateString() : '',
                row.kind,
                row.ref || '',
                row.status || '',
                row.payment || '',
                row.items || '',
                `₹${(Number(row.tax) || 0).toFixed(2)}`,
                `₹${(Number(row.total) || 0).toFixed(2)}`
              ])}
            />
          )}
        </section>

        <ClinicalReports patientId={patient._id} visits={history} />
      </div>
      {selectedVisit && (
        <DoctorAppointmentDetailModal
          appointment={{ ...selectedVisit, PATIENT_ID: selectedVisit.PATIENT_ID || patient }}
          onClose={() => setSelectedVisit(null)}
          onSave={() => { setSelectedVisit(null); loadChart(); }}
          readOnly={!canEdit}
        />
      )}
    </div>
  );
};

function canEditVisits() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const names = new Set([user.role, ...(user.roles || []).map((role) => role.ROLE_NAME)].filter(Boolean));
    return names.has('Doctor') || names.has('Admin');
  } catch (error) {
    return false;
  }
}

function vitalSummary(vitals) {
  if (!vitals) return '—';
  const parts = [];
  if (vitals.BLOOD_PRESSURE) parts.push(vitals.BLOOD_PRESSURE);
  if (vitals.PULSE) parts.push(`P ${vitals.PULSE}`);
  if (vitals.TEMPERATURE) parts.push(`${vitals.TEMPERATURE}°F`);
  if (vitals.SPO2) parts.push(`SpO2 ${vitals.SPO2}%`);
  if (vitals.WEIGHT) parts.push(`${vitals.WEIGHT} kg`);
  return parts.length ? parts.join(', ') : '—';
}

function ChartTable({ headers, rows }) {
  if (!rows.length) return <p style={{ margin: 0, color: '#6c757d' }}>Nothing recorded yet.</p>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr>
            {headers.map((header) => <th key={header || 'action'} style={{ padding: '8px', borderBottom: '1px solid #dee2e6', textAlign: 'left', backgroundColor: '#f8f9fa' }}>{header}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => <td key={cellIndex} style={{ padding: '8px', borderBottom: '1px solid #f1f3f5', verticalAlign: 'top' }}>{cell || '—'}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: 'white',
  padding: '24px',
  borderRadius: '8px',
  width: '94%',
  maxWidth: '1080px',
  maxHeight: '92%',
  overflowY: 'auto',
  position: 'relative',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
};

const panelStyle = { marginTop: '1rem', padding: '1rem', border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#fff' };
const sectionTitle = { margin: '0 0 0.75rem', color: '#1f4d40' };
const fieldStyle = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', boxSizing: 'border-box', backgroundColor: '#f8f9fa' };
const closeButton = { padding: '8px 14px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const openButton = { padding: '4px 10px', backgroundColor: '#1f4d40', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };

export default PatientDetailModal;
